import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronRight, 
  Award, 
  Briefcase,
  Users,
  BarChart,
  FileText,
  ClipboardList,
  Target,
  Layers,
  ClockIcon,
  Building2,
  Share,
  Brain,
  ArrowLeft,
  UserRoundPlus,
} from 'lucide-react';
import { Button } from '../components/common/Button';
import { ParticleBackground } from '../components/ParticleBackground';
import { InterviewRoundCard } from '../components/interviews/InterviewRoundCard';
import { ScheduleInterviewModal } from '../components/interviews/ScheduleInterviewModal';
import { InterviewFeedbackModal } from '../components/interviews/InterviewFeedbackModal';
import { useToast } from '../components/common/Toast';
import { withPageTransition, PageTransitionItem } from '../utils/withTransition';
import { useResponsive } from '../contexts/ResponsiveContext';
import interviewService, { SeriesDataResponse, RecommendedInterview, UserInterviewProgress } from '../services/interviewService';
import useEnrollmentManager from '../hooks/useEnrollmentManager';
import useSeriesProgress from '../hooks/useSeriesProgress';
import { InterviewFeedback } from '../types/interviews';

// Default for companies without a logo
const DEFAULT_LOGO = 'https://images.seeklogo.com/logo-png/34/1/swiggy-logo-png_seeklogo-348257.png';

// Create a module-level cache to track series data fetches across component remounts
const seriesFetchCache: Record<string, boolean> = {};


interface SeriesProgressData {
  totalInterviews: number;
  completed: number;
  inProgress: number;
  pending: number;
  completionPercentage: number;
  userInterviews: UserInterviewProgress[];
}

// Custom background gradient styles
const backgroundGradient = {
  background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
  position: 'relative' as 'relative',
  zIndex: 0,
  overflow: 'hidden' as 'hidden'
};

// Helper function to calculate average score from user interviews
const calculateAverageScore = (userInterviews: UserInterviewProgress[]): number => {
  const completedInterviews = userInterviews.filter(
    interview => interview.status === 'completed' && typeof interview.score === 'number'
  );
  
  if (completedInterviews.length === 0) return 0;
  
  const totalScore = completedInterviews.reduce(
    (sum, interview) => sum + (interview.score || 0), 
    0
  );
  
  return Math.round(totalScore / completedInterviews.length);
};

const InterviewSeriesDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  // Use correct URL parameter based on route path
  const params = useParams();
  const seriesId = params.seriesId || params.id; // Try both possible parameter names
  console.log("URL params:", params, "seriesId extracted:", seriesId);
  
  const toast = useToast();
  const { isMobile: isMobileView } = useResponsive();
  
  // Use our custom hooks for enrollment and progress
  const { isEnrolled, enrollInSeries } = useEnrollmentManager();
  const { currentProgress, initializeProgress } = useSeriesProgress(seriesId);
  
  // State
  const [seriesData, setSeriesData] = useState<SeriesDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedRound, setSelectedRound] = useState<RecommendedInterview | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<InterviewFeedback | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [hasCompanyLogo, setHasCompanyLogo] = useState(false);
  const [isUserEnrolledState, setIsUserEnrolledState] = useState<boolean>(false);
  const [cancelLoading, setCancelLoading] = useState<string | null>(null);
  const [userProgressData, setUserProgressData] = useState<SeriesProgressData | null>(null);
  
  // Calculate if user is enrolled in this series
  const isUserEnrolled = useMemo(() => {
    return seriesId ? isEnrolled(seriesId) || isUserEnrolledState : false;
  }, [seriesId, isEnrolled, isUserEnrolledState]);
  
  // Calculate progress percentage with useMemo - now using the API response data
  const progressPercentage = useMemo(() => {
    if (!isUserEnrolled) return 0;
    
    if (userProgressData) {
      return userProgressData.completionPercentage;
    }
    
    if (seriesData && seriesData.interviews && currentProgress) {
      return Math.round((currentProgress.completedRounds / seriesData.interviews.length) * 100);
    }
    
    return 0;
  }, [isUserEnrolled, userProgressData, currentProgress, seriesData]);
  
  // Create a safe version of current progress for use in places where it might be null
  const safeProgress = useMemo(() => {
    if (userProgressData) {
      return {
        completedRounds: userProgressData.completed,
        totalRounds: userProgressData.totalInterviews,
        lastActivity: new Date().toISOString(),
        averageScore: calculateAverageScore(userProgressData.userInterviews),
        roundScores: {}
      };
    }
    
    return currentProgress || {
      completedRounds: 0,
      totalRounds: seriesData?.interviews?.length || 0,
      lastActivity: new Date().toISOString(),
      averageScore: 0,
      roundScores: {}
    };
  }, [currentProgress, seriesData, userProgressData]);
  
  // Memoize the toast functions to prevent dependency changes
  const memoizedShowToast = useCallback(
    (message: string, type: any) => toast.showToast(message, type),
    [toast]
  );

  // Handle share button click
  const handleShare = useCallback(() => {
    navigator.clipboard.writeText(window.location.href);
    memoizedShowToast('Link copied to clipboard', 'success');
  }, [memoizedShowToast]);

  // Reference to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  
  // Create fetchSeriesData function outside useEffect to make it accessible elsewhere
  const fetchSeriesData = useCallback(async () => {
    if (!seriesId) {
      console.error('No seriesId found in URL params');
      return;
    }
    
    // Mark as fetching in the cache immediately
    seriesFetchCache[seriesId] = true;
    
    // Set a timeout to reset the cache if data isn't loaded
    // This prevents getting stuck in a loading state
    const cacheResetTimeout = setTimeout(() => {
      if (isMountedRef.current && seriesData === null) {
        console.log(`Cache reset triggered for series ${seriesId} due to timeout`);
        seriesFetchCache[seriesId] = false;
        // Force a re-render to trigger fetch
        setIsLoading(prev => {
          // Only update if still loading to prevent toggle
          if (prev) return false;
          return prev;
        });
      }
    }, 10000); // 10 second timeout
    
    setIsLoading(true);
    
    try {
      console.log(`Fetching series details for ID: "${seriesId}"`);
      const data = await interviewService.getSeriesDetails(seriesId);
      console.log('Series data received:', data);
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setSeriesData(data);
      
        // Check if user is enrolled
        console.log(`Checking enrollment for series ID: "${seriesId}"`);
        const isEnrolled = await interviewService.checkEnrollment(seriesId) as boolean;
        console.log('Enrollment status:', isEnrolled);
        
        if (isMountedRef.current) {
          setIsUserEnrolledState(isEnrolled);
          
          // Only fetch user progress if user is enrolled
          if (isEnrolled) {
            try {
              const seriesDetailsResponse = await interviewService.getUserSeriesDetails(seriesId);
              console.log('Series details response:', seriesDetailsResponse);
              
              if (seriesDetailsResponse && seriesDetailsResponse.userProgress) {
                setUserProgressData(seriesDetailsResponse.userProgress);
                console.log('User progress data loaded:', seriesDetailsResponse.userProgress);
              }
            } catch (error) {
              console.error('Error fetching user progress:', error);
              // Continue without progress data - enrollment is still valid
            }
          }
          
          setIsLoading(false);
        }
      }
      
      // Clear the timeout since data was loaded successfully
      clearTimeout(cacheResetTimeout);
    } catch (error) {
      console.error("Error fetching series details:", error);
      if (isMountedRef.current) {
        memoizedShowToast('Failed to load series details', 'error');
        setIsLoading(false);
        // Reset cache on error so we can try again
        seriesFetchCache[seriesId] = false;
      }
      
      // Clear the timeout on error
      clearTimeout(cacheResetTimeout);
    }
  }, [seriesId, memoizedShowToast, seriesData]);

  useEffect(() => {
    // Set mounted flag
    isMountedRef.current = true;
    
    // Debug log to ensure the effect is running
    console.log('InterviewSeriesDetailsPage: useEffect triggered', { seriesId });
    
    if (!seriesId) {
      console.error('No seriesId found in URL params');
      return;
    }
    
    // Check if we've already fetched this series data in this session AND data is loaded in state
    // Only skip fetch if we have both cached the request AND have data in state
    if (seriesFetchCache[seriesId] && seriesData !== null) {
      console.log(`Data for series ${seriesId} already loaded, skipping fetch`);
      return;
    }
    
    fetchSeriesData();
    
    // Cleanup function
    return () => {
      // Mark component as unmounted
      isMountedRef.current = false;
    };
  }, [seriesId, fetchSeriesData, seriesData]);
  
  // Get interview status from userProgress data if available
  const getInterviewStatus = useCallback((interviewId: string): { 
    status: 'completed' | 'scheduled' | 'available' | 'not_started' | 'in_progress' | 'locked',
    language?: string,
    score?: number 
  } => {
    if (!userProgressData || !userProgressData.userInterviews) {
      return { status: 'locked' };
    }
    
    const interview = userProgressData.userInterviews.find(
      ui => ui.interview === interviewId
    );
    
    if (!interview) {
      return { status: 'locked' };
    }
    
    // If interview is explicitly locked, return locked status
    if (interview.locked === true) {
      return { status: 'locked' };
    }
    
    // Map API statuses to UI statuses
    switch(interview.status) {
      case 'completed':
        return { 
          status: 'completed',
          score: interview.score,
          language: interview.language
        };
      case 'in_progress':
        return { status: 'in_progress' };
      case 'not_started':
        // When not_started and not locked, it should be available
        return { status: 'available' };
      case 'cancelled':
        return { status: 'available' };
      default:
        return { status: 'locked' };
    }
  }, [userProgressData]);
  
  const handleEnrollInSeries = async () => {
    if (!seriesId || !seriesData) return;
    
    setIsEnrolling(true);
    try {
      const enrollmentResponse = await interviewService.enrollInSeries(seriesId);
      console.log('Enrollment successful:', enrollmentResponse);
      
      // Update local enrollment state
      const enrolled = enrollInSeries(seriesId);
      setIsUserEnrolledState(true);
      
      // Fetch user progress data after enrollment
      try {
        const seriesDetailsResponse = await interviewService.getUserSeriesDetails(seriesId);
        if (seriesDetailsResponse && seriesDetailsResponse.userProgress) {
          setUserProgressData(seriesDetailsResponse.userProgress);
          console.log('User progress data loaded after enrollment:', seriesDetailsResponse.userProgress);
        }
      } catch (error) {
        console.error('Error fetching user progress after enrollment:', error);
        // Don't fail the entire enrollment if progress fetch fails
      }
      
      // Initialize progress data for the hooks
      if (enrolled) {
        initializeProgress(seriesId, seriesData.interviews?.length || 0);
      }
      
      memoizedShowToast('Successfully enrolled in series', 'success');
      // Don't navigate away - let user see the updated state
    } catch (error: any) {
      console.error("Error enrolling in series:", error);
      
      // Show specific error message from API if available
      const errorMessage = error.message || 'Failed to enroll in series';
      memoizedShowToast(errorMessage, 'error');
    } finally {
      setIsEnrolling(false);
    }
  };
  
  // Handle scheduling/rescheduling an interview
  const handleScheduleInterview = async (round: RecommendedInterview, date: Date, language?: string) => {
    try {
      
      // Check if we're rescheduling (update existing) or scheduling for the first time (create new)
      if (round.status === 'not_started' && round.userInterviewId) {
        // Reschedule existing interview
        const updatedInterview = await interviewService.rescheduleInterview(round.userInterviewId, date.toISOString(), language);
        
        // Update the UI to reflect the rescheduled interview
        if (seriesData) {
          const updatedInterviews = [...seriesData.interviews];
          const interviewIndex = updatedInterviews.findIndex(i => i._id === round._id);
          
          if (interviewIndex !== -1) {
            updatedInterviews[interviewIndex] = {
              ...updatedInterviews[interviewIndex],
              scheduledAt: date.toISOString(),
              status: 'scheduled'
            };
            
            setSeriesData({
              ...seriesData,
              interviews: updatedInterviews
            });

            // Update the userProgressData with the new scheduled interview
            setUserProgressData({
              ...userProgressData,
              totalInterviews: userProgressData?.totalInterviews || 0,
              completed: userProgressData?.completed || 0,
              inProgress: userProgressData?.inProgress || 0,
              pending: userProgressData?.pending || 0,
              completionPercentage: userProgressData?.completionPercentage || 0,
              userInterviews: userProgressData?.userInterviews.map(ui => ui.interview === round._id ? { ...ui, status: 'scheduled', scheduledAt: date.toISOString() } : ui) || []
            });
          }
        }
        
        memoizedShowToast('Interview rescheduled successfully', 'success');
        
        // Show additional toast for Google Calendar if it was updated
        if (updatedInterview?.metadata?.calendarEventId) {
          memoizedShowToast('Interview updated in Google Calendar', 'info');
        }
      } else {
        // New scheduling - use service method
        const data = await interviewService.scheduleInterview(
          round._id, 
          date.toISOString(), 
          language, 
          seriesId // Pass the series ID
        );
        
        // Update the UI to reflect the scheduled interview
        if (seriesData) {
          // Create a copy of interviews array
          const updatedInterviews = [...seriesData.interviews];
          const interviewIndex = updatedInterviews.findIndex(i => i._id === round._id);
          
          if (interviewIndex !== -1 && data) {
            // Update the status and scheduledAt
            updatedInterviews[interviewIndex] = {
              ...updatedInterviews[interviewIndex],
              status: 'scheduled',
              scheduledAt: date.toISOString(),
              userInterviewId: data._id // Store the interview ID for future operations
            };
            
            console.log('handleScheduleInterview -> updatedInterviews', updatedInterviews);
            // Update the seriesData with the new interviews array
            setSeriesData({
              ...seriesData,
              interviews: updatedInterviews
            });

            // Update the userProgressData with the new scheduled interview
            setUserProgressData({
              ...userProgressData,
              totalInterviews: userProgressData?.totalInterviews || 0,
              completed: userProgressData?.completed || 0,
              inProgress: userProgressData?.inProgress || 0,
              pending: userProgressData?.pending || 0,
              completionPercentage: userProgressData?.completionPercentage || 0,
              userInterviews: userProgressData?.userInterviews.map(ui => ui.interview === round._id ? { ...ui, status: 'scheduled', scheduledAt: date.toISOString() } : ui) || []
            });
          }
        }
        
        memoizedShowToast('Interview scheduled successfully', 'success');
        
        // Show additional toast for Google Calendar if it was added
        if (data?.metadata?.calendarEventId) {
          memoizedShowToast('Interview added to Google Calendar', 'info');
        }
      }
    } catch (error) {
      console.error('Error scheduling/rescheduling interview:', error);
      memoizedShowToast('Failed to schedule interview', 'error');
    } finally {
      setShowScheduleModal(false);
    }
  };
  
  // Handle cancelling a scheduled interview
  const handleCancelInterview = async (round: RecommendedInterview) => {
    if (!round.userInterviewId) {
      memoizedShowToast('Cannot cancel interview: missing interview ID', 'error');
      return;
    }
    
    try {
      setCancelLoading(round._id || null);
      
      // Use service method
      const response = await interviewService.cancelInterview(round.userInterviewId);
      
      // Check if a calendar event was removed
      if (response?.calendarEventRemoved) {
        memoizedShowToast('Interview cancelled and removed from Google Calendar', 'info');
      } else {
        memoizedShowToast('Interview cancelled successfully', 'success');
      }
      
      // Update the UI to reflect the cancelled interview
      if (seriesData) {
        // Create a copy of interviews array
        const updatedInterviews = [...seriesData.interviews];
        const interviewIndex = updatedInterviews.findIndex(i => i._id === round._id);
        
        if (interviewIndex !== -1) {
          // Reset the status back to 'available' and clear scheduledAt
          updatedInterviews[interviewIndex] = {
            ...updatedInterviews[interviewIndex],
            status: 'not_started', // Or we can make it 'cancelled'
            scheduledAt: undefined,
          };
          
          // Update the seriesData with the new interviews array
          setSeriesData({
            ...seriesData,
            interviews: updatedInterviews
          });
        }
      }
      
      // Update userProgressData to reflect the cancelled status
      if (userProgressData) {
        setUserProgressData({
          ...userProgressData,
          userInterviews: userProgressData.userInterviews.map(ui => 
            ui.interview === round._id 
              ? { ...ui, status: 'cancelled', scheduledAt: undefined }
              : ui
          )
        });
      }
    } catch (error) {
      console.error('Error cancelling interview:', error);
      memoizedShowToast('Failed to cancel interview', 'error');
    } finally {
      setCancelLoading(null);
    }
  };
  
  // Open scheduling modal for a round
  const handleScheduleClick = (round: RecommendedInterview) => {
    setSelectedRound(round);
    setShowScheduleModal(true);
  };
  
  const handleViewFeedback = (round: RecommendedInterview) => {
    if (round._id && round.feedback) {
      // Find the corresponding userInterview to get minutes used and completion date
      const userInterview = userProgressData?.userInterviews?.find(ui => ui.interview === round._id);
      
      // Enrich the feedback data with interview details for the modal
      const enrichedFeedback = {
        ...round.feedback,
        interviewDetails: {
          title: round.title || 'Interview',
          jobRole: seriesData?.role || 'Role not specified',
          company: seriesData?.company || 'Company not specified',
          minutesUsed: round?.minutesUsed || 0, // Minutes actually used in the interview
          scheduledAt: round.scheduledAt || userInterview?.completedAt || userInterview?.startedAt || new Date().toISOString(),
          duration: round.durationMinutes || 0,
          difficulty: round.difficulty || 0,
          userFeedbackRating: userInterview?.userFeedbackRating || null,
          language: round.language || 'English',
          tags: round.tags || [],
          status: round.status || 'not_started',
          voicePack: round.voicePack,
          characterGender: round.characterGender,
          interviewPrompt: round.interviewPrompt,
        }
      };
      
      console.log('[InterviewSeriesDetailsPage] Opening feedback modal with enriched data:', {
        title: enrichedFeedback.interviewDetails.title,
        company: enrichedFeedback.interviewDetails.company,
        jobRole: enrichedFeedback.interviewDetails.jobRole,
        minutesUsed: enrichedFeedback.interviewDetails.minutesUsed,
        scheduledAt: enrichedFeedback.interviewDetails.scheduledAt
      });
      
      setSelectedFeedback(enrichedFeedback);
      setShowFeedbackModal(true);
    } else {
      memoizedShowToast('No feedback available for this interview', 'info');
    }
  };
  
  // Handle joining an interview and updating status to 'in_progress'
  const handleJoinInterview = async (round: RecommendedInterview) => {
    
    try {
      console.log('round status', round.status);
      
      // For reattempt (completed interviews), don't pass any existing IDs to create a new slot
      const isReattempt = round.status === 'completed';
      const interviewId = isReattempt ? round._id : (round.status === 'not_started' || round.status === 'available' ? round._id : round.userInterviewId);
      
      // Build interview details object
      const interviewDetailsToSend = {
        scheduledInterviewId: isReattempt ? undefined : round.userInterviewId, // This is the userInterviewId which means user's scheduled interview id
        id: round._id,
        title: round.title,
        company: seriesData?.company,
        companyLogo: seriesData?.companyLogo || seriesData?.companyLogoUrl,
        companyId: seriesData?.companyId,
        jobRole: seriesData?.role,
        duration: round.durationMinutes,
        description: round.description,
        tags: round.tags,
        status: round.status || 'not_started',
        voicePack: round.voicePack,
        characterGender: round.characterGender,
        interviewPrompt: round.interviewPrompt,
        createdAt: round.createdAt,
        updatedAt: round.updatedAt,
        language: round.language || round.languageName || 'English',
        newInterview: round.status === 'not_started' || round.status === 'available' || !round.status || isReattempt,
        isReattempt: isReattempt,
        series: seriesData ? {
          _id: seriesData._id,
          name: seriesData.name,
          description: seriesData.description
        } : undefined
      };
      
      
      // Navigate to the interview preview page
      navigate(`/interview-preview/${interviewId}`, {
        state: {
          interviewDetails: interviewDetailsToSend
        }
      });
    } catch (error) {
      console.error('Error joining interview:', error);
      memoizedShowToast('Failed to join interview', 'error');
    }
  };
  
  // Update where we check for company logo
  useEffect(() => {
    // Check if there's a company logo
    setHasCompanyLogo(
      Boolean(seriesData?.companyLogoUrl || seriesData?.company)
    );
  }, [seriesData]);
  
  const roundsWithStatus = useMemo(() => {
    if (!seriesData || !seriesData.interviews) return [];

    console.log('[InterviewSeriesDetailsPage] Building rounds with status for', seriesData.interviews.length, 'interviews');
    console.log('[InterviewSeriesDetailsPage] userProgressData:', JSON.stringify(userProgressData, null, 2));

    return seriesData.interviews.map(round => {
      let roundStatus: string = 'available';
      let roundLanguage: string | undefined;
      let scheduledAt: string | undefined;
      let userInterviewId: string | undefined;
      let feedback: any = undefined;
      let minutesUsed: number | undefined;

      console.log(`[InterviewSeriesDetailsPage] Processing round ${round._id}:`, {
        title: round.title,
        voicePack: round.voicePack,
        characterGender: round.characterGender,
        interviewPrompt: round.interviewPrompt
      });

      if (isUserEnrolled && userProgressData?.userInterviews) {
        const userInterview = userProgressData.userInterviews.find(ui => ui.interview === round._id);
        if (userInterview) {
          roundStatus = userInterview.status;
          roundLanguage = userInterview.language;
          scheduledAt = userInterview.startedAt || userInterview.completedAt;
          // Set userInterviewId from the userProgress data
          userInterviewId = userInterview.userInterviewId;
          // Set feedback data for completed interviews
          feedback = userInterview.feedback;
          // Set minutesUsed from userInterview data
          minutesUsed = userInterview.minutesUsed;
          
          console.log(`[InterviewSeriesDetailsPage] Found userInterview for ${round._id}:`, {
            status: userInterview.status,
            userInterviewId: userInterview.userInterviewId,
            hasVoicePack: !!userInterview.voicePack,
            hasCharacterGender: !!userInterview.characterGender,
            hasInterviewPrompt: !!userInterview.interviewPrompt
          });
          
          // CRUCIAL: Use interview details from userProgressData if available (backend now populates this)
          if (userInterview.voicePack !== undefined) {
            round.voicePack = userInterview.voicePack;
          }
          if (userInterview.characterGender !== undefined) {
            round.characterGender = userInterview.characterGender;
          }
          if (userInterview.interviewPrompt !== undefined) {
            round.interviewPrompt = userInterview.interviewPrompt;
          }
          
          console.log(`[InterviewSeriesDetailsPage] Updated round ${round._id} with userInterview data:`, {
            voicePack: round.voicePack,
            characterGender: round.characterGender,
            hasInterviewPrompt: !!round.interviewPrompt
          });
        } else {
          console.log(`[InterviewSeriesDetailsPage] No userInterview found for ${round._id}`);
        }
      }
      
      const finalRound = {
        ...round,
        durationMinutes: round.durationMinutes ?? 0, 
        difficulty: round.difficulty ?? 0,
        status: roundStatus as 'completed' | 'scheduled' | 'available' | 'not_started' | 'in_progress' | 'locked',
        language: roundLanguage || 'English', 
        scheduledAt: scheduledAt, 
        userInterviewId: userInterviewId, 
        feedback: feedback || undefined,
        minutesUsed: minutesUsed,
        companyLogo: seriesData.companyLogoUrl || seriesData.companyLogo || DEFAULT_LOGO,
        matchScore: round.matchScore, 
      };
      
      console.log(`[InterviewSeriesDetailsPage] Final round ${round._id} data:`, {
        title: finalRound.title,
        voicePack: finalRound.voicePack,
        characterGender: finalRound.characterGender,
        hasInterviewPrompt: !!finalRound.interviewPrompt
      });
      
      return finalRound;
    });
  }, [seriesData, isUserEnrolled, userProgressData]);
  
  // Sort rounds: locked first, then by their original order in seriesData.interviews
  const sortedRounds = useMemo(() => {
    return roundsWithStatus.sort((a, b) => {
      if (a.status === 'locked') return 1;
      if (b.status === 'locked') return -1;
      return 0;
    });
  }, [roundsWithStatus]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark" style={backgroundGradient}>
        <ParticleBackground />
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-primary-light border-t-transparent rounded-full mb-4 mx-auto"></div>
          <p className="text-text-secondary-light dark:text-text-secondary-dark mb-2">Loading interview series details...</p>
          {/* <Button 
            variant="outline"
            size="md"
            className="w-full"
            onClick={() => {
              // Reset cache and force reload
              if (seriesId) {
                seriesFetchCache[seriesId] = false;
                setIsLoading(true);
                setSeriesData(null);
                // Force re-render to trigger fetch
                setTimeout(() => {
                  fetchSeriesData();
                }, 100);
              }
            }}
          >
            Refresh
          </Button> */}
        </div>
      </div>
    );
  }
  
  if (!seriesData) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
        <ParticleBackground />
        <div className="container mx-auto px-4 py-24 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-heading font-bold mb-2 text-text-primary-light dark:text-text-primary-dark">
              Series Not Found
            </h2>
            <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6 text-base">
              We couldn't find the interview series you're looking for.<br /> The series might have been deleted or is not available.
            </p>
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => navigate('/')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      
      <main className="container mx-auto px-4 pt-32 pb-16">
        <div className="max-w-7xl mx-auto">
           <div className="flex justify-between items-start mb-8 hover:cursor-pointer" onClick={() => navigate(-1)}>
            <div className="flex items-center gap-2">
              <ArrowLeft size={14} className="text-text-secondary-light dark:text-text-secondary-dark" />
              <span className="text-text-secondary-light dark:text-text-secondary-dark font-medium text-base">Back to Series</span>
            </div>
          </div>
          {/* Series Header with Company Logo */}
          <PageTransitionItem index={1}>
            <div className="flex flex-col md:flex-row gap-6 items-start mb-8">
              {/* Only show logo container if there is a logo */}
              {hasCompanyLogo && (
              <div className="p-2 w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-background-light dark:bg-background-light flex items-center justify-center flex-shrink-0 border border-border-light dark:border-border-dark">
                <img 
                    src={seriesData.companyLogoUrl || DEFAULT_LOGO} 
                  alt={`${seriesData.company} logo`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              )}
              
              <div className="flex-1">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                    {seriesData.name.length > 42 ? seriesData.name.slice(0, 42) + '...' : seriesData.name}
                 </h1>
                  
                  <div className={`flex ${isMobileView ? 'hidden' : 'flex-row'} mt-2 md:mt-0 mb-4 md:mb-0 items-center gap-2`}>
                    {!isUserEnrolled && (
                      <Button 
                        variant="primary" 
                        className="px-6"
                        loading={isEnrolling}
                        disabled={isEnrolling}
                        onClick={handleEnrollInSeries}
                        size="md"
                      leftIcon={<UserRoundPlus size={16} />}
                    >
                      {isEnrolling ? 'Joining...' : 'Join Series'}
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="md" 
                    className="py-3 px-5" 
                    onClick={handleShare}
                    leftIcon={<Share size={16} />}>
                    Share
                  </Button>
                  </div>
                </div>
                
                <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4 max-w-3xl font-medium text-base">
                  {seriesData.description}
                </p>
                
                
                {/* Tags and Match Percentage */}
                <div className="flex flex-wrap gap-3">
                  {/* Show match score only if it exists */}
                  {(seriesData.matchScore != null || seriesData.match_score != null) && (
                  <div className="inline-flex items-center text-sm bg-green-500/10 dark:bg-green-500/10 px-3 py-1 rounded-full border border-green-500/20 dark:border-green-500/20">
                    <Target size={14} className="mr-1.5 text-green-500 dark:text-green-500" />
                    <span className="text-green-700 dark:text-green-400 font-medium text-sm">
                        {(((seriesData.matchScore ?? seriesData.match_score) ?? 0) * 100).toFixed(1)}% Match
                    </span>
                  </div>
                  )}
                  
                  <div className="inline-flex items-center text-sm bg-primary-light/10 dark:bg-primary-dark/10 px-3 py-1 rounded-full border border-primary-light/20 dark:border-primary-dark/20">
                    <Briefcase size={14} className="mr-1.5 text-primary-light dark:text-primary-dark" />
                    <span className="text-primary-light dark:text-primary-dark font-medium text-sm">
                      {seriesData.role}
                    </span>
                  </div>

                  <div className="inline-flex items-center text-sm bg-purple-500/10 dark:bg-purple-400/10 px-3 py-1 rounded-full border border-purple-500/20 dark:border-purple-400/20">
                    <Building2 size={14} className="mr-1.5 text-purple-600 dark:text-purple-400" />
                    <span className="text-purple-600 dark:text-purple-400 font-medium text-sm">
                      {seriesData.company}
                    </span>
                  </div>
                  
                  <div className="inline-flex items-center text-sm bg-amber-500/5 dark:bg-amber-400/5 px-3 py-1 rounded-full border border-amber-500/10 dark:border-amber-400/10">
                    <div className="flex items-center">
                    <Brain size={14} className={`mr-1 ${seriesData.difficulty <= 2 ? "text-green-600 dark:text-green-400" : seriesData.difficulty <= 4 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`} />
                    <span className={`text-sm mr-1 ${seriesData.difficulty <= 2 ? "text-green-600 dark:text-green-400" : seriesData.difficulty <= 4 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`}>
                      Difficulty:
                    </span>
                      <span className={`font-medium text-sm ${
                        seriesData.difficulty <= 2 ? "text-green-600 dark:text-green-400" : 
                        seriesData.difficulty <= 4 ? "text-amber-600 dark:text-amber-400" : 
                        "text-red-600 dark:text-red-400"
                      }`}>
                        {seriesData.difficulty <= 2 ? "Easy" : 
                         seriesData.difficulty <= 4 ? "Medium" : 
                         "Hard"}
                      </span>
                    </div>
                  </div>
                  
                  
                </div>
                <div className={`flex ${isMobileView ? 'flex-row' : 'hidden'} mt-6 md:mt-0  items-center gap-2`}>
                    {!isUserEnrolled && (
                      <Button 
                        variant="primary" 
                        className="px-6"
                        loading={isEnrolling}
                        disabled={isEnrolling}
                        onClick={handleEnrollInSeries}
                        size="md"
                      leftIcon={<UserRoundPlus size={16} />}
                    >
                      {isEnrolling ? 'Joining...' : 'Join Series'}
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="md" 
                    className="py-3 px-5" 
                    onClick={handleShare}
                    leftIcon={<Share size={16} />}>
                    Share
                  </Button>
                  </div>
              </div>
            </div>
          </PageTransitionItem>
          
          {/* Series Stats Grid */}
          <PageTransitionItem index={2}>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-background-light/50 dark:bg-background-dark/50 p-4 rounded-xl border border-border-light dark:border-border-dark backdrop-blur-sm">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-3">
                    <Layers className="w-5 h-5 text-primary-light dark:text-primary-dark" />
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Total Rounds</p>
                    <p className="font-medium text-text-primary-light dark:text-text-primary-dark">{seriesData.interviews?.length || 0} Interviews</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-background-light/50 dark:bg-background-dark/50 p-4 rounded-xl border border-border-light dark:border-border-dark backdrop-blur-sm">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-3">
                    <ClockIcon className="w-5 h-5 text-primary-light dark:text-primary-dark" />
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Estimated Interview Duration</p>
                    <p className="font-medium text-text-primary-light dark:text-text-primary-dark">{seriesData.totalTimeMinutes} minutes</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-background-light/50 dark:bg-background-dark/50 p-4 rounded-xl border border-border-light dark:border-border-dark backdrop-blur-sm">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-3">
                    <Users className="w-5 h-5 text-primary-light dark:text-primary-dark" />
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Candidates Prepared</p>
                    <p className="font-medium text-text-primary-light dark:text-text-primary-dark">{seriesData.enrollmentCount?.toLocaleString() || '0'}</p>
                  </div>
                </div>
              </div>
            </div>
          </PageTransitionItem>
          
          {/* Skills Match Card */}
          <PageTransitionItem index={3}>
            <div className="mb-8 bg-background-light/50 dark:bg-background-dark/50 p-4 md:p-6 rounded-xl border border-border-light dark:border-border-dark backdrop-blur-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex-shrink-0">
                  <FileText className="w-6 h-6 text-primary-light dark:text-primary-dark" />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                    Position Details
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark mb-2">Job Description</h4>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4 leading-relaxed">
                         {seriesData.roleDescription || 'No job description available'}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">Prepare in advance</p>
                      <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                        <div className="flex items-start gap-2 mb-1.5">
                          <ClockIcon size={14} className="mt-0.5 text-primary-light dark:text-primary-dark mt-1" />
                          <span>Average preparation time: <span className="text-text-primary-light dark:text-text-primary-dark font-medium">{seriesData.preparationTimeWeeks} {!seriesData.preparationTimeWeeks?.includes('week') ? 'weeks' : ''}</span></span>
                        </div>
                        <div className="flex items-start gap-2 mb-1.5">
                          <ChevronRight size={14} className="mt-0.5 text-green-700 dark:text-green-400" />
                          <span>Required Pass Score: <span className="text-green-700 dark:text-green-400 font-medium">{seriesData.passScore || seriesData.requiredPassScore}%</span></span>
                        </div>
                        <div className="flex items-start gap-2">
                          <Award size={14} className="mt-0.5 text-amber-500 dark:text-amber-400" />
                          <span>Success rate: <span className="text-amber-500 dark:text-amber-400 font-medium">{seriesData.successRate}%</span> of candidates passed this series</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </PageTransitionItem>
          
          {/* User Progress Section - Only show if user is enrolled */}
          {isUserEnrolled && (
            <PageTransitionItem index={4}>
              <div className="mb-8 bg-background-light/50 dark:bg-background-dark/50 p-4 md:p-6 rounded-xl border border-border-light dark:border-border-dark backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex-shrink-0">
                    <ClipboardList className="w-6 h-6 text-primary-light dark:text-primary-dark" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-1">
                          Your Progress
                        </h3>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          Complete all rounds to master the interview process
                        </p>
                      </div>
                      <div className="flex items-center mt-2 md:mt-0">
                        <span className="text-base font-medium text-text-primary-light dark:text-text-primary-dark mr-2">
                          {safeProgress.completedRounds}/{seriesData.interviews?.length || 0} round{seriesData.interviews?.length > 1 ? 's' : ''}
                        </span>
                        <span className="text-base font-medium text-text-primary-light dark:text-text-primary-dark">
                          ({progressPercentage}% complete)
                        </span>
                      </div>
                    </div>
                    
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2">
                      <div 
                        className="bg-primary-light dark:bg-primary-dark h-2.5 rounded-full" 
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                    
                      <div className="mt-4 flex items-center">
                        <div className="p-2 rounded-full bg-amber-100/30 dark:bg-amber-900/20 mr-3">
                          <BarChart className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Average Score</p>
                        <p className="font-medium text-text-primary-light dark:text-text-primary-dark">{safeProgress.averageScore.toFixed(1)}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </PageTransitionItem>
          )}
          
          {/* Interview Rounds Section */}
          <PageTransitionItem index={5}>
            <div className="bg-background-light/50 dark:bg-background-dark/50 p-4 md:p-6 rounded-xl border border-border-light dark:border-border-dark backdrop-blur-sm mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex-shrink-0">
                  <Layers className="w-6 h-6 text-primary-light dark:text-primary-dark" />
                </div>
                
                <div>
                  <h3 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-1">
                    Interview Rounds
                  </h3>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    {isUserEnrolled 
                      ? 'Schedule and complete each interview round to master the entire process'
                      : 'Join series to start preparing for these interview rounds'}
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                {sortedRounds.map((round, index) => {
                  // Get status from userProgress data if available, else fallback to previous logic
                  const interviewStatus = getInterviewStatus(round._id);
                  
                  // Log status for debugging
                  console.log(`Round ${index + 1} (${round._id}) status:`, interviewStatus);
                  
                  // Check if previous round is completed or if this is the first round (always available)
                  const previousRoundCompleted = index === 0 ? true : 
                    getInterviewStatus(seriesData.interviews[index - 1]?._id)?.status === 'completed';
                  
                  // For first round or if previous round is completed, the status should be available
                  // unless it's explicitly marked as completed, scheduled, or in_progress
                  let roundStatus = interviewStatus.status;
                  let roundLanguage = interviewStatus.language;
                  
                  // If it's the first round and not already scheduled/completed, it should be available
                  if (index === 0 && interviewStatus.status === 'locked' && isUserEnrolled) {
                    roundStatus = 'available';
                  }
                  
                  // Default to the fallback logic if we can't determine from API
                  if (!roundStatus || roundStatus === 'locked') {
                    roundStatus = isUserEnrolled ? 
                      (index < safeProgress.completedRounds ? 
                        'completed' : 
                        (index === safeProgress.completedRounds || previousRoundCompleted ? 'available' : 'locked')
                      ) : 
                      'locked';
                  }
                      
                  return (
                    <InterviewRoundCard
                      key={round._id}
                      round={{
                        id: round._id,
                        title: round.title,
                        description: round.description,
                        duration: round.durationMinutes ?? 0,
                        difficulty: round.difficulty ?? 0,
                        status: roundStatus as 'completed' | 'scheduled' | 'available' | 'not_started' | 'in_progress' | 'locked',
                        language: roundLanguage || 'English',
                        scheduledDate: round.scheduledAt,
                        userInterviewId: round.userInterviewId,
                        companyLogo: round.companyLogo,
                        score: round.matchScore,
                        feedback: round.feedback || undefined
                      }}
                      roundNumber={index + 1}
                      totalRounds={seriesData.interviews?.length || 0}
                      onSchedule={() => handleScheduleClick(round)}
                      onViewFeedback={() => handleViewFeedback(round)}
                      onCancel={() => handleCancelInterview(round)}
                      onJoin={() => handleJoinInterview(round)}
                      isEnrolled={isUserEnrolled}
                      cancelLoading={cancelLoading === round._id}
                      previousRoundCompleted={previousRoundCompleted}
                      isActive={round.isActive !== false}
                    />
                  );
                })}
              </div>
            </div>
          </PageTransitionItem>
          
        </div>
      </main>
      
      {showScheduleModal && selectedRound && (
        <ScheduleInterviewModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          interviewDetails={{
            id: selectedRound._id,
            title: selectedRound.title,
            company: seriesData.company,
            jobRole: seriesData.role,
            duration: selectedRound.durationMinutes,
            description: selectedRound.description
          }}
          mode={selectedRound.status === 'not_started' ? 'reschedule' : 'schedule'}
          initialDate={selectedRound.scheduledAt}
          onSchedule={async (date: Date, language?: string) => {
            console.log(`Scheduled interview for ${date.toISOString()}`);
            await handleScheduleInterview(selectedRound, date, language);
          }}
        />
      )}
      
      {showFeedbackModal && selectedFeedback && (
        <InterviewFeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          feedback={selectedFeedback as InterviewFeedback}
        />
      )}
    </div>
  );
};

InterviewSeriesDetailsPage.displayName = 'InterviewSeriesDetailsPage';

// Export with page transition HOC
// Use React.memo to prevent unnecessary re-renders of the component
export default React.memo(withPageTransition(InterviewSeriesDetailsPage)); 